import { useEffect, useMemo, useRef } from 'react';

// We use the raw Statsig client (not fg/expVal from @atlaskit/platform-feature-flags)
// because this is a Statsig *experiment* read by its `isEnabled` parameter and we
// need manual exposure control (getExperimentValue with fireExperimentExposure +
// manuallyLogExperimentExposure). `fg` only reads boolean gates, and `expVal` lives
// in @atlaskit/tmp-editor-statsig and is typed to editor experiments only. This
// matches the existing pattern in ActiveTabAwareChatHeader/History in this package.
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { CHAT_STORE_STATUS } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { getLastHumanMessageFromConversation } from '../../../controllers/chat-store/getLastHumanMessageFromConversation';
import { useCurrentConversation } from '../../../controllers/chat-store/useCurrentConversation';
import { useCurrentConversationId } from '../../../controllers/chat-store/useCurrentConversationId';

import type { SetSizeArgs, SetSizeResult } from './use-mini-modal-resize';

const EXPERIMENT_NAME = 'rovo_minimodal_auto_expand_on_chat';
const EXPERIMENT_PARAM = 'isEnabled';

/** Target size (px) the mini-modal auto-expands toward when a chat begins. */
const TARGET_WIDTH = 450;
const TARGET_HEIGHT = 700;

export interface UseMiniModalAutoExpandArgs {
	setSize: (args: SetSizeArgs) => SetSizeResult;
	isResizeEnabled: boolean;
}

/**
 * Auto-expands the Rovo mini-modal toward a comfortable size (450x700) when a
 * chat begins (TREX-1422), gated behind `isResizeEnabled` and the
 * `rovo_minimodal_auto_expand_on_chat` experiment.
 *
 * The grow fires on the "no human message -> has a human message" edge, derived
 * from the actual conversation messages (not the conversation id). This covers
 * both sending a chat in a fresh modal and opening directly onto an existing
 * conversation, while never expanding into an empty, message-less modal
 * (TREX-1726). It relies on `setSize`'s per-axis don't-shrink rule, so re-firing
 * never shrinks a user's manually enlarged modal.
 *
 * Exposure is logged manually only for users who actually experience (Test) or
 * would experience (Control) a grow, keeping the variants symmetric.
 */
export const useMiniModalAutoExpand = ({
	setSize,
	isResizeEnabled,
}: UseMiniModalAutoExpandArgs): void => {
	// Emptiness is derived from the actual chat messages, NOT from the conversation
	// id. When the modal opens directly onto a conversation, the chat store assigns
	// a real `currentConversationId` before the history has loaded, so an id-only
	// check reports "non-empty" while there are still zero messages — causing a
	// premature grow into a modal full of awkward whitespace with no greeting
	// (TREX-1726). Reading the real messages, plus waiting for any in-flight
	// history fetch to settle, avoids that race.
	//
	// Messages live in the dedicated `useChatMessages` store keyed by conversation
	// id — NOT on `Conversation.messages` (which stays empty). We only read
	// `useCurrentConversation` for its load `status`.
	const conversationId = useCurrentConversationId();
	const currentConversation = useCurrentConversation();
	const [messages] = useChatMessages({ conversationId });
	const isFetchInFlight = currentConversation?.status === CHAT_STORE_STATUS.loadingChat;
	// Memoised so the human-message scan only re-runs when the messages array
	// reference changes, not on every unrelated re-render.
	const hasHumanMessage = useMemo(
		() => getLastHumanMessageFromConversation(messages) !== undefined,
		[messages],
	);

	// Tracks whether the conversation had a human message on the previous settled
	// render, so we grow exactly once on the no-message -> has-message edge.
	const hadHumanMessageRef = useRef(false);
	// Whether we've observed the first settled (not-in-flight) render yet. Used
	// only to label the analytics source: the very first settled render that
	// already has a message is a "mount into non-empty chat"; any later edge is a
	// "first message transition". It never affects whether we grow.
	const hasSettledOnceRef = useRef(false);

	// Stable ref so the trigger helper and effect don't need setSize in deps.
	const setSizeRef = useRef(setSize);
	setSizeRef.current = setSize;

	const runAutoExpandTrigger = (source: SetSizeArgs['source']): void => {
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
		const isInTest = FeatureGates.getExperimentValue<boolean>(
			EXPERIMENT_NAME,
			EXPERIMENT_PARAM,
			false,
			{ fireExperimentExposure: false },
		);

		// Control uses a dry-run so the "would grow" check applies the identical
		// viewport-clamp / don't-shrink logic as the real Test resize — otherwise
		// a viewport too small to reach the target would over-count Control.
		const result = setSizeRef.current({
			targetWidth: TARGET_WIDTH,
			targetHeight: TARGET_HEIGHT,
			source,
			dryRun: !isInTest,
		});

		if (result.widthGrew || result.heightGrew) {
			FeatureGates.manuallyLogExperimentExposure(EXPERIMENT_NAME);
		}
	};

	// Single source of truth for auto-expand: grow whenever the conversation goes
	// from "no human message" to "has a human message". A single effect (rather
	// than separate mount + transition effects) avoids any chance of both firing
	// for the same edge and double-counting the grow / exposure.
	//
	// This deliberately keys on the presence of a real human message rather than
	// the conversation id. When the modal opens directly onto a conversation, the
	// store assigns a real `currentConversationId` before the history loads, so an
	// id-only check would report "non-empty" while there are still zero messages —
	// growing into an empty, whitespace-filled modal with no greeting (TREX-1726).
	//
	// It covers both cases with one rule:
	//   - Send a chat in a fresh modal: false -> true edge fires the grow.
	//   - Open onto an existing conversation (deep-link/reload): the initial
	//     `hadHumanMessageRef` is false and, once the in-flight fetch settles with
	//     messages, the same false -> true edge fires the grow exactly once.
	// While the history fetch is in flight we hold off updating the baseline so a
	// transient empty render can't be mistaken for a genuine "no message" state.
	useEffect(() => {
		if (!isResizeEnabled) {
			return;
		}
		if (isFetchInFlight) {
			return;
		}
		if (!hadHumanMessageRef.current && hasHumanMessage) {
			// Label the source by whether the message was already present at the
			// first settled render (opened onto a non-empty chat) vs appeared later
			// (the user sent a chat). This is analytics-only; the grow is identical.
			runAutoExpandTrigger(
				hasSettledOnceRef.current ? 'first-message-transition' : 'mount-into-non-empty-chat',
			);
		}
		hadHumanMessageRef.current = hasHumanMessage;
		hasSettledOnceRef.current = true;
		// runAutoExpandTrigger is recreated every render and is intentionally
		// omitted from deps. This is only safe because every version of it reads
		// solely stable refs (setSizeRef) and the FeatureGates global — no
		// captured reactive values. Keep it that way: if this function ever
		// closes over a prop/state value, that value must be added to deps here.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasHumanMessage, isFetchInFlight, isResizeEnabled]);
};
